import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetIdentity, useNotification, useOne } from "@refinedev/core";
import { User, ApiKeyOption } from "../../interfaces/user";
import {
  IMDFFile,
  IMDFData,
  GeoJSONFile,
  LayerControl,
} from "../../types/imdf.types";
import JSZip from "jszip";
import EnhancedLeafletMap from "../../components/map/EnhancedLeafletMap";
import IMDFFileList from "../../components/imdf/IMDFFileList";
import IMDFStatsChart from "../../components/imdf/IMDFStatsChart";
import ProposalEditor from "../../components/proposal/ProposalEditor";
import { usePermission } from "../../components/PermissionGuard";

// IMDF 相關類型已移至 ../../types/imdf.types.ts

const ViewDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user } = useGetIdentity<User>();
  const { open: openNotification } = useNotification();
  const { isMaintainer } = usePermission();

  const [file, setFile] = useState<IMDFFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [availableApiKeys, setAvailableApiKeys] = useState<ApiKeyOption[]>([]);
  const [selectedApiKey, setSelectedApiKey] = useState<string>("");

  // 新增 IMDF 檔案處理相關狀態
  const [imdfData, setImdfData] = useState<IMDFData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [layerControl, setLayerControl] = useState<LayerControl>({
    venue: true,
    building: true,
    level: true,
    unit: true,
    amenity: true,
    opening: true,
    fixture: true,
    anchor: true,
  });
  const [selectedFeature, setSelectedFeature] =
    useState<GeoJSON.Feature | null>(null);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  
  // 提案編輯相關狀態
  const [showProposalEditor, setShowProposalEditor] = useState(false);
  const [proposalSuccess, setProposalSuccess] = useState<string | null>(null);

  // 處理圖層控制變更
  const handleLayerControlChange = useCallback(
    (newLayerControl: LayerControl) => {
      setLayerControl(newLayerControl);
    },
    []
  );

  // 處理 feature 點擊（來自地圖或列表）
  const handleFeatureClick = useCallback((feature: GeoJSON.Feature) => {
    setSelectedFeature(feature);
  }, []);

  // 使用 useOne hook 取得檔案資料
  const { data: fileResponse, isLoading: isFileLoading } = useOne<IMDFFile>({
    resource: "imdf",
    id: id!,
  });

  // 工具函數：格式化檔案大小
  const formatFileSize = (bytes: number): string => {
    const sizes = ["B", "KB", "MB", "GB"];
    let i = 0;
    let size = bytes;
    while (size >= 1024 && i < sizes.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(1)} ${sizes[i]}`;
  };


  // IMDF 檔案下載和處理邏輯 (移植自 imdf/detail.tsx)
  const downloadAndProcessFile = useCallback(async () => {
    if (!file) return;

    console.log("Starting downloadAndProcessFile...");
    setIsProcessing(true);
    setError(null);

    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://imdf.sto-tek.com";
      const token = localStorage.getItem("token");
      console.log("now user: " + JSON.stringify(user));

      // 統一使用 imdf/files 端點，因為這是唯一正確的下載方式
      const downloadUrl = `${API_URL}/imdf/files/${file.id}/download`;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      console.log("Downloading from:", downloadUrl);

      const response = await fetch(downloadUrl, {
        method: "GET",
        headers,
      });

      let blob: Blob;

      if (!response.ok) {
        // 如果是 admin/maintainer 使用 API Key 失敗，嘗試回退到認證下載
        if (
          user &&
          ["admin", "maintainer"].includes(user.role) &&
          selectedApiKey
        ) {
          console.log(
            "API Key download failed, trying authenticated download..."
          );
          const fallbackUrl = `${API_URL}/imdf/files/${file.id}/download`;
          const fallbackResponse = await fetch(fallbackUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!fallbackResponse.ok) {
            throw new Error(`認證下載也失敗: ${fallbackResponse.status}`);
          }

          blob = await fallbackResponse.blob();
          console.log("Fallback blob size:", blob.size);
        } else {
          throw new Error(`下載失敗: ${response.status}`);
        }
      } else {
        blob = await response.blob();
        console.log("Blob size:", blob.size);
      }

      // 使用 JSZip 解壓縮檔案
      const zip = new JSZip();
      const zipFile = await zip.loadAsync(blob);

      console.log("ZIP file loaded, contents:", Object.keys(zipFile.files));

      const geoJsonFiles: GeoJSONFile[] = [];
      const fileTypes: string[] = [];
      const allFeatures: GeoJSON.Feature[] = [];
      let totalFeatures = 0;

      // 處理 ZIP 檔案中的每個檔案
      for (const fileName in zipFile.files) {
        const file = zipFile.files[fileName];
        if (file.dir) continue; // 跳過目錄

        console.log("Processing file:", fileName);
        const fileExtension = fileName.split(".").pop()?.toLowerCase();

        if (fileExtension === "geojson") {
          try {
            const content = await file.async("text");
            const geoJson = JSON.parse(content);

            const features = geoJson.features || [];
            const featureCount = features.length;

            // 為每個 feature 添加檔案來源資訊
            const enhancedFeatures = features.map(
              (feature: GeoJSON.Feature) => ({
                ...feature,
                properties: {
                  ...feature.properties,
                  _sourceFile: fileName,
                },
              })
            );

            geoJsonFiles.push({
              name: fileName,
              features: enhancedFeatures,
              featureCount,
            });

            allFeatures.push(...enhancedFeatures);
            totalFeatures += featureCount;

            // 記錄檔案類型
            const baseFileName = fileName.replace(".geojson", "");
            if (!fileTypes.includes(baseFileName)) {
              fileTypes.push(baseFileName);
            }

            console.log(`Processed ${fileName}: ${featureCount} features`);
          } catch (err) {
            console.error(`Error processing ${fileName}:`, err);
          }
        }
      }

      // 建立合併的 GeoJSON
      const combinedGeoJson: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: allFeatures,
      };

      const processedData: IMDFData = {
        files: geoJsonFiles,
        totalFeatures,
        fileTypes,
        combinedGeoJson,
      };

      console.log("IMDF data processed:", processedData);
      setImdfData(processedData);

      openNotification?.({
        type: "success",
        message: "檔案處理完成",
        description: `成功解析 ${geoJsonFiles.length} 個 GeoJSON 檔案，共 ${totalFeatures} 個 features`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "處理失敗";
      console.error("Processing error:", err);
      setError(errorMessage);
      openNotification?.({
        type: "error",
        message: "檔案處理錯誤",
        description: errorMessage,
      });
    } finally {
      setIsProcessing(false);
    }
  }, [file, user, selectedApiKey, openNotification]);

  // 取得可用的 API Keys (僅限 admin/maintainer)
  const fetchAvailableApiKeys = useCallback(async () => {
    if (!user || !["admin", "maintainer"].includes(user.role)) return;

    try {
      const token = localStorage.getItem("token");
      const API_URL =
        import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";

      const response = await fetch(`${API_URL}/projects`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("無法取得專案列表");
      }

      const data = await response.json();
      const projects = data.data || [];

      const apiKeyOptions: ApiKeyOption[] = projects.map(
        (project: { id: string; name: string; apiKey: string }) => ({
          apiKey: project.apiKey,
          projectName: project.name,
          projectId: project.id,
        })
      );

      setAvailableApiKeys(apiKeyOptions);

      // 如果用戶有預設的 apikey，設為選中狀態
      if (user.apikey) {
        setSelectedApiKey(user.apikey);
      } else if (apiKeyOptions.length > 0) {
        setSelectedApiKey(apiKeyOptions[0].apiKey);
      }
    } catch (err) {
      console.error("載入 API Keys 失敗:", err);
    }
  }, [user]);

  // fetchFileDetail 函數已移除，改用 useOne hook 和 downloadAndProcessFile

  // 下載檔案
  const handleDownload = async () => {
    if (!file || !user) return;

    try {
      setDownloading(true);
      const API_URL =
        import.meta.env.VITE_API_URL ?? "http://imdf.sto-tek.com/";
      const token = localStorage.getItem("token");

      // 根據用戶角色選擇下載方式
      const { downloadUrl, headers } = (["admin", "maintainer"].includes(user.role) && selectedApiKey) 
        ? {
            // Admin/Maintainer: 優先使用 API Key 下載
            downloadUrl: `${API_URL}/projects/downloadIMDF?apikey=${selectedApiKey}&uuid=${file.id}`,
            headers: {} as Record<string, string>
          }
        : {
            // 一般用戶或回退方案: 使用認證下載
            downloadUrl: `${API_URL}/imdf/files/${file.id}/download`,
            headers: { "Authorization": `Bearer ${token}` } as Record<string, string>
          };

      const response = await fetch(downloadUrl, {
        headers,
      });

      let blob: Blob;

      if (!response.ok) {
        // 如果是 admin/maintainer API Key 下載失敗，嘗試認證下載
        if (["admin", "maintainer"].includes(user.role) && selectedApiKey) {
          const fallbackUrl = `${API_URL}/imdf/files/${file.id}/download`;
          const fallbackResponse = await fetch(fallbackUrl, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!fallbackResponse.ok) {
            throw new Error("下載失敗");
          }

          blob = await fallbackResponse.blob();
        } else {
          throw new Error("下載失敗");
        }
      } else {
        blob = await response.blob();
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      openNotification?.({
        type: "success",
        message: "下載成功",
        description: "檔案下載已開始",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "下載失敗";
      openNotification?.({
        type: "error",
        message: "下載錯誤",
        description: errorMessage,
      });
    } finally {
      setDownloading(false);
    }
  };

  // 同步 useOne 的檔案資料到本地狀態
  useEffect(() => {
    if (fileResponse?.data) {
      setFile(fileResponse.data);
      setLoading(false);
    }
  }, [fileResponse]);

  // 初始化 API Keys (僅限 admin/maintainer)
  useEffect(() => {
    if (user && ["admin", "maintainer"].includes(user.role)) {
      fetchAvailableApiKeys();
    }
  }, [user, fetchAvailableApiKeys]);

  // 當檔案資料準備好時，開始處理檔案
  useEffect(() => {
    if (file && user) {
      // 對於 admin/maintainer，需要等待 selectedApiKey 設置
      if (["admin", "maintainer"].includes(user.role)) {
        if (selectedApiKey) {
          downloadAndProcessFile();
        }
      } else {
        // 一般用戶直接處理
        downloadAndProcessFile();
      }
    }
  }, [file, user, selectedApiKey, downloadAndProcessFile]);

  // 提案相關處理函數
  const handleProposalSave = (proposal: any) => {
    console.log('Proposal saved:', proposal);
    setProposalSuccess('提案已儲存');
    setTimeout(() => setProposalSuccess(null), 3000);
  };

  const handleProposalSubmit = (proposalId: number) => {
    setShowProposalEditor(false);
    setProposalSuccess('提案已提交審核，請等待 Admin 處理');
    setTimeout(() => setProposalSuccess(null), 5000);
  };

  const handleProposalCancel = () => {
    setShowProposalEditor(false);
  };

  const getStatusColor = (status: string) => {
    const statusColors = {
      pending: "bg-yellow-100 text-yellow-800",
      validating: "bg-blue-100 text-blue-800",
      valid: "bg-green-100 text-green-800",
      invalid: "bg-red-100 text-red-800",
    };
    return (
      statusColors[status as keyof typeof statusColors] ||
      "bg-gray-100 text-gray-800"
    );
  };

  const getStatusText = (status: string) => {
    const statusText = {
      pending: "待處理",
      validating: "驗證中",
      valid: "有效",
      invalid: "無效",
    };
    return statusText[status as keyof typeof statusText] || status;
  };

  // 顯示載入狀態
  if (loading || isFileLoading || isProcessing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">
            {isProcessing ? "正在處理 IMDF 檔案..." : "載入中..."}
          </p>
        </div>
      </div>
    );
  }

  // 顯示錯誤狀態
  if (error || !file) {
    return (
      <div className="space-y-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/view")}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            返回列表
          </button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">載入錯誤</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error || "找不到指定的檔案"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4">
      {/* 標題列和控制 */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/view")}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              返回列表
            </button>

            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {file.originalName}
              </h1>
              <div className="flex items-center space-x-4 mt-1">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                    file.status
                  )}`}
                >
                  {getStatusText(file.status)}
                </span>
                <span className="text-sm text-gray-500">
                  {formatFileSize(file.fileSize)}
                </span>
                {imdfData && (
                  <span className="text-sm text-gray-500">
                    {imdfData.files.length} 檔案 • {imdfData.totalFeatures}{" "}
                    Features
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Admin/Maintainer API Key 選擇器 */}
            {user &&
              ["admin", "maintainer"].includes(user.role) &&
              availableApiKeys.length > 0 && (
                <div className="flex flex-col space-y-1">
                  <label
                    htmlFor="apikey-select"
                    className="text-xs font-medium text-gray-700"
                  >
                    API Key
                  </label>
                  <select
                    id="apikey-select"
                    value={selectedApiKey}
                    onChange={(e) => setSelectedApiKey(e.target.value)}
                    className="min-w-[180px] px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availableApiKeys.map((option) => (
                      <option key={option.apiKey} value={option.apiKey}>
                        {option.projectName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {/* Maintainer 修改建議按鈕 */}
            {isMaintainer() && imdfData && (
              <button
                onClick={() => setShowProposalEditor(true)}
                className="px-4 py-2 rounded-md text-sm font-medium transition-colors bg-purple-600 hover:bg-purple-700 text-white"
              >
                提出修改建議
              </button>
            )}

            <button
              onClick={handleDownload}
              disabled={downloading}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                !downloading
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {downloading ? "下載中..." : "下載檔案"}
            </button>
          </div>
        </div>
      </div>

      {/* 主要內容區域 */}
      {imdfData ? (
        <>
          {/* 地圖檢視 */}
          <EnhancedLeafletMap
            imdfData={imdfData}
            layerControl={layerControl}
            onLayerControlChange={handleLayerControlChange}
            selectedFeature={selectedFeature}
            onFeatureClick={handleFeatureClick}
            height="500px"
            className="shadow-sm rounded-lg overflow-hidden"
          />

          {/* 分檔資訊和統計圖表 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <IMDFFileList
              imdfData={imdfData}
              onFeatureClick={handleFeatureClick}
              selectedFeature={selectedFeature}
              expandedFiles={expandedFiles}
              onExpandedFilesChange={setExpandedFiles}
              className="lg:col-span-1"
            />

            <IMDFStatsChart 
              imdfData={imdfData} 
              expandedFiles={expandedFiles}
              className="lg:col-span-1" 
            />
          </div>

          {/* 檔案詳細資訊 */}
          {file.description && (
            <div className="bg-white shadow-sm rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                檔案描述
              </h3>
              <p className="text-gray-600">{file.description}</p>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">版本：</span>
                  <span className="text-gray-900">
                    {file.version || "未指定"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">分類：</span>
                  <span className="text-gray-900">
                    {file.category || "未指定"}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">建立時間：</span>
                  <span className="text-gray-900">
                    {new Date(file.createdAt).toLocaleDateString("zh-TW")}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">更新時間：</span>
                  <span className="text-gray-900">
                    {new Date(file.updatedAt).toLocaleDateString("zh-TW")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white shadow-sm rounded-lg p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.712-3.714M14 40v-4a9.971 9.971 0 01.712-3.714M34 20a6 6 0 11-12 0 6 6 0 0112 0zM22 20a6 6 0 11-12 0 6 6 0 0112 0z"
              />
            </svg>
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            正在處理檔案
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            請稍候，正在解析 IMDF 檔案內容...
          </p>
        </div>
      )}

      {/* 提案編輯器模態視窗 */}
      {showProposalEditor && imdfData?.combinedGeoJson && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50" style={{ zIndex: 9999 }}>
          <div className="h-full w-full">
            <ProposalEditor
              imdfFileId={id || ''}
              geoJsonData={imdfData.combinedGeoJson}
              onSave={handleProposalSave}
              onSubmit={handleProposalSubmit}
              onCancel={handleProposalCancel}
            />
          </div>
        </div>
      )}

      {/* 成功訊息 */}
      {proposalSuccess && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md shadow-lg z-50">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {proposalSuccess}
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewDetailPage;
